import { useMemo } from 'react';

import { runningInRNWebView, useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';

import { useConfig } from '../defaultConfig';
import { useStepUpStatus } from '../fetchHooks/useStepUpStatus';

type STEPUP_CONFIGURATION_TYPE =
    | 'high-assurance'
    | 'action-required'
    | 'allowed'
    | 'not-configured'
    | 'not-allowed'
    | 'not-supported';

const needsToStepup: STEPUP_CONFIGURATION_TYPE[] = ['action-required'];
const cannotStepup: STEPUP_CONFIGURATION_TYPE[] = ['not-configured', 'not-allowed', 'not-supported'];

const useStepUp = (conditions: boolean[] = [], remoteFeatureToggle = true) => {
    const { stepUpAuthLink, featureToggles } = useConfig();
    const { supports } = useReactNativeProps();
    const { isSupported, isEnabled } = useMemo(() => {
        const fulfillsAllConditions = conditions.every((condition) => condition);
        return {
            isSupported: Boolean(supports?.stepUpAuth) && runningInRNWebView,
            isEnabled:
                Boolean(stepUpAuthLink) && featureToggles.enableStepUp && remoteFeatureToggle && fulfillsAllConditions,
        };
    }, [conditions, supports?.stepUpAuth, stepUpAuthLink, featureToggles.enableStepUp, remoteFeatureToggle]);

    const { data: stepUpStatus } = useStepUpStatus({ enabled: isEnabled && isSupported });

    const status = useMemo((): STEPUP_CONFIGURATION_TYPE => {
        const stepupConfigured = isEnabled && isSupported;
        const actionRequired = stepupConfigured && Boolean(stepUpStatus?.allowed) && !stepUpStatus?.highAssurance;
        const isHighAssurance = stepupConfigured && stepUpStatus?.highAssurance;

        if (!isSupported) return 'not-supported';
        if (!isEnabled) return 'not-configured';

        if (isHighAssurance) return 'high-assurance';
        if (actionRequired) return 'action-required';
        if (!stepUpStatus?.allowed) return 'not-allowed';
        if (stepupConfigured) return 'allowed';

        return 'not-allowed';
    }, [isEnabled, stepUpStatus, isSupported]);

    return {
        canStepup: !cannotStepup.includes(status) && Boolean(stepUpStatus?.allowed),
        showStepupConfiguration: needsToStepup.includes(status),
        isHighAssurance: status === 'high-assurance',
    };
};

export default useStepUp;
